/**
 * useMovieDetails.js - Custom hook for fetching movie details
 *
 * This hook provides movie data fetching functionality using the working
 * AppLoadContext authentication system and React 19 patterns.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import spotlightService from '../services/SpotlightService';

/**
 * Hook for fetching and managing movie details
 *
 * @param {string} channelIdentifier - The movie ID or slug to fetch
 * @returns {Object} Movie data and state
 * @returns {Object} .movie - The movie data
 * @returns {Array} .relatedContent - Related content for the movie
 * @returns {boolean} .loading - Whether data is currently loading
 * @returns {string} .error - Error message if fetch failed
 * @returns {Function} .retry - Function to retry fetching data
 */
export const useMovieDetails = channelIdentifier => {
  const [movie, setMovie] = useState(null);
  const [relatedContent, setRelatedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetching = useRef(false);
  const [error, setError] = useState(null);

  // Determine if the identifier is a videoId (MongoDB ObjectId format) or a slug
  const isVideoId = channelIdentifier && /^[0-9a-fA-F]{24}$/.test(channelIdentifier);

  const fetchMovieDetails = useCallback(async () => {
    if (!channelIdentifier) {
      setLoading(false);
      return;
    }

    if (fetching.current) {return;}
    fetching.current = true;
    try {
      setLoading(true);
      setError(null);

      let movieData;
      
      if (isVideoId) {
        // Use SpotlightService.getChannelById for videoId
        // Fetching by videoId
        movieData = await spotlightService.getChannelById(channelIdentifier);
                  // Movie data received (by ID)
        setMovie(movieData);
      } else {
        // Use SpotlightService.getChannelBySlug for slug
        // Fetching by slug
        const data = await spotlightService.getChannelBySlug(channelIdentifier).catch(e => {
          throw new Error(e);
        });
                  // Movie data received (by slug)
        movieData = data.channels[0];
        setMovie(movieData);
      }

      // Fetch related content - use slug if available, otherwise try with ID
      const relatedIdentifier = movieData?.slug || channelIdentifier;
      try {
        const relatedResults = await spotlightService
          .getRelatedChannelsByChannelSlug(relatedIdentifier)
          .catch(e => {
            console.warn('Failed to fetch related content:', e);
            return { channels: [] };
          });
        if (Array.isArray(relatedResults?.channels) && relatedResults.channels.length) {
          setRelatedContent(relatedResults.channels);
        }
      } catch (relatedError) {
        console.warn('Error fetching related content:', relatedError);
        // Don't fail the whole request if related content fails
      }
    } catch (err) {
      console.error('Error fetching movie details:', err);
      setError("We couldn't load this movie right now. Please try again.");
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, [channelIdentifier, isVideoId]);

  // Fetch data on mount and when movieId changes
  useEffect(() => {
    fetchMovieDetails();
  }, [channelIdentifier]);

  return {
    movie,
    relatedContent,
    loading,
    error,
    retry: fetchMovieDetails,
  };
};

export default useMovieDetails;
